/**
 * 1:1 Meeting Assistant API Client
 * Frontend API functions for interacting with the backend
 */

import type {
  TeamMember,
  Meeting,
  MeetingWithMember,
  ActionItem,
  CreateTeamMemberRequest,
  UpdateTeamMemberRequest,
  CreateMeetingRequest,
  UpdateMeetingRequest,
  CreateActionItemRequest,
  UpdateActionItemRequest,
  SuggestedTopic,
} from './types';

const BASE_URL = '/api/secure/one-on-one';

// Cache CSRF token
let csrfToken: string | null = null;

/**
 * Fetch CSRF token from the server
 */
async function fetchCsrfToken(): Promise<string | null> {
  try {
    const response = await fetch('/api/secure/csrf-token', {
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('[OneOnOne] Failed to fetch CSRF token');
      return null;
    }

    const data = await response.json();
    csrfToken = data.csrfToken || null;
    return csrfToken;
  } catch (error) {
    console.error('[OneOnOne] Failed to fetch CSRF token:', error);
    return null;
  }
}

/**
 * Generic API call wrapper with error handling and CSRF token support
 */
async function apiCall<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));

    if (response.status === 403 && error.error === 'CSRF validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
        const retryResponse = await fetch(url, {
          ...options,
          credentials: 'include',
          headers,
        });
        if (retryResponse.ok) {
          return retryResponse.json();
        }
      }
    }

    throw new Error(error.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Team Member Operations
// ============================================================================

/**
 * List all team members for the current user
 */
export async function listTeamMembers(): Promise<TeamMember[]> {
  const data = await apiCall<{ team_members: TeamMember[] }>(`${BASE_URL}/team-members`);
  return data.team_members;
}

/**
 * Get a single team member by ID
 */
export async function getTeamMember(id: string): Promise<TeamMember> {
  const data = await apiCall<{ team_member: TeamMember }>(`${BASE_URL}/team-members/${id}`);
  return data.team_member;
}

/**
 * Create a new team member
 */
export async function createTeamMember(data: CreateTeamMemberRequest): Promise<TeamMember> {
  const result = await apiCall<{ team_member: TeamMember }>(`${BASE_URL}/team-members`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.team_member;
}

/**
 * Update a team member
 */
export async function updateTeamMember(id: string, data: UpdateTeamMemberRequest): Promise<TeamMember> {
  const result = await apiCall<{ team_member: TeamMember }>(`${BASE_URL}/team-members/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return result.team_member;
}

/**
 * Delete a team member
 */
export async function deleteTeamMember(id: string): Promise<void> {
  await apiCall<{ success: boolean }>(`${BASE_URL}/team-members/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Meeting Operations
// ============================================================================

/**
 * List meetings for a team member
 */
export async function listMeetings(teamMemberId?: string): Promise<MeetingWithMember[]> {
  const url = teamMemberId
    ? `${BASE_URL}/meetings?team_member_id=${teamMemberId}`
    : `${BASE_URL}/meetings`;
  const data = await apiCall<{ meetings: MeetingWithMember[] }>(url);
  return data.meetings;
}

/**
 * Get upcoming meetings
 */
export async function getUpcomingMeetings(): Promise<MeetingWithMember[]> {
  const data = await apiCall<{ meetings: MeetingWithMember[] }>(`${BASE_URL}/meetings?upcoming=true`);
  return data.meetings;
}

/**
 * Get a single meeting by ID
 */
export async function getMeeting(id: string): Promise<MeetingWithMember> {
  const data = await apiCall<{ meeting: MeetingWithMember }>(`${BASE_URL}/meetings/${id}`);
  return data.meeting;
}

/**
 * Create a new meeting
 */
export async function createMeeting(data: CreateMeetingRequest): Promise<Meeting> {
  const result = await apiCall<{ meeting: Meeting }>(`${BASE_URL}/meetings`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.meeting;
}

/**
 * Update a meeting
 */
export async function updateMeeting(id: string, data: UpdateMeetingRequest): Promise<Meeting> {
  const result = await apiCall<{ meeting: Meeting }>(`${BASE_URL}/meetings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return result.meeting;
}

/**
 * Delete a meeting
 */
export async function deleteMeeting(id: string): Promise<void> {
  await apiCall<{ success: boolean }>(`${BASE_URL}/meetings/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Complete a meeting
 */
export async function completeMeeting(id: string, summary?: string): Promise<Meeting> {
  const result = await apiCall<{ meeting: Meeting }>(`${BASE_URL}/meetings/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify({ summary }),
  });
  return result.meeting;
}

// ============================================================================
// Action Item Operations
// ============================================================================

/**
 * List action items (optionally filtered by meeting or status)
 */
export async function listActionItems(options?: {
  meetingId?: string;
  status?: string;
  teamMemberId?: string;
}): Promise<ActionItem[]> {
  const params = new URLSearchParams();
  if (options?.meetingId) params.set('meeting_id', options.meetingId);
  if (options?.status) params.set('status', options.status);
  if (options?.teamMemberId) params.set('team_member_id', options.teamMemberId);

  const url = `${BASE_URL}/action-items${params.toString() ? `?${params}` : ''}`;
  const data = await apiCall<{ action_items: ActionItem[] }>(url);
  return data.action_items;
}

/**
 * Get pending action items for dashboard
 */
export async function getPendingActionItems(): Promise<ActionItem[]> {
  return listActionItems({ status: 'pending' });
}

/**
 * Create a new action item
 */
export async function createActionItem(data: CreateActionItemRequest): Promise<ActionItem> {
  const result = await apiCall<{ action_item: ActionItem }>(`${BASE_URL}/action-items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.action_item;
}

/**
 * Update an action item
 */
export async function updateActionItem(id: string, data: UpdateActionItemRequest): Promise<ActionItem> {
  const result = await apiCall<{ action_item: ActionItem }>(`${BASE_URL}/action-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return result.action_item;
}

/**
 * Delete an action item
 */
export async function deleteActionItem(id: string): Promise<void> {
  await apiCall<{ success: boolean }>(`${BASE_URL}/action-items/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Mark an action item as complete
 */
export async function completeActionItem(id: string): Promise<ActionItem> {
  return updateActionItem(id, { status: 'completed' });
}

// ============================================================================
// AI Operations
// ============================================================================

/**
 * Generate AI-suggested topics for a meeting
 */
export async function generateSuggestedTopics(meetingId: string): Promise<SuggestedTopic[]> {
  const result = await apiCall<{ suggested_topics: SuggestedTopic[] }>(
    `${BASE_URL}/meetings/${meetingId}/ai/suggest-topics`,
    { method: 'POST' }
  );
  return result.suggested_topics;
}

// ============================================================================
// Export Operations
// ============================================================================

/**
 * Export meeting notes as Markdown
 */
export async function exportMeetingNotes(meetingId: string): Promise<string> {
  const response = await fetch(`${BASE_URL}/meetings/${meetingId}/export/markdown`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to export meeting notes');
  }

  return response.text();
}

/**
 * Download meeting notes as Markdown file
 */
export async function downloadMeetingNotes(meetingId: string, teamMemberName: string): Promise<void> {
  const markdown = await exportMeetingNotes(meetingId);
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `1on1-${teamMemberName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
